import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Tab,
  Tabs,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import axios, { AxiosError } from "axios";
import { useMutation, useQuery } from "react-query";
import { API_URL } from "../consts";
import { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import { Risorse, RisorseSpecifiche, RisorseTipologie } from "../prismaTypes";
import { getString } from "../utils/getString";
import { useNavigate, useParams } from "react-router-dom";
import { queryClient } from "../App";
import { TabellaDettagli } from "./TabellaDettagli";
import { oraPrenotaStore } from "../oraPrenotaStore";
import { uiStore } from "../uiStore";
import { Loading } from "../ui/Loading";
import { DispositiviUi } from "./TabellaDispositivi";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div role="tabpanel" hidden={value !== index} id={`simple-tabpanel-${index}`} {...other}>
      <Box mt={1}>{value === index && children}</Box>
    </div>
  );
}
function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const defaultValues = {
  denominazione: "",
  indirizzo: "",
  Risorse_TipologieId: null,
  Risorse_SpecificheId: null,
  descrizione: "",
  attiva: false,
  idDomotica: null,
  dispoLuci: false,
  dispoRiscaldamento: false,
  loraDeviceLuci: null,
  loraDeviceRiscaldamento: null,
  anticipoLuci: 0,
  posticipoLuci: 0,
  anticipoRiscaldamento: 0,
  durataRiscaldamento: null,
};
type Inputs = {
  id?: number;
  ClubId: number;
  denominazione: string | null;
  indirizzo: string | null;
  Risorse_TipologieId: number | null;
  Risorse_SpecificheId: number | null;
  descrizione: string | null;
  attiva: boolean;
  idDomotica: number | null;
  dispoLuci: boolean;
  dispoRiscaldamento: boolean;
};
const schema = yup
  .object({
    attiva: yup.boolean(),
    loraDeviceLuci: yup
      .string()
      .max(4)
      .nullable(true)
      .transform((val) => (val !== "" ? val : null)),
    loraDeviceRiscaldamento: yup
      .string()
      .max(4)
      .nullable(true)
      .transform((val) => (val !== "" ? val : null)),
    anticipoLuci: yup.number().min(0),
    posticipoLuci: yup.number().min(0),
    anticipoRiscaldamento: yup.number().min(0),
    durataRiscaldamento: yup
      .number()
      .min(0)
      .nullable(true)
      .transform((val) => (val === Number(val) ? val : null)),
    Risorse_TipologieId: yup.number().min(0),
    Risorse_SpecificheId: yup.number().min(0),
  })
  .required();

export function Risorsa() {
  let { id, ClubId } = useParams();
  let clubId = parseInt(ClubId ?? "0");

  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const { refetch } = useQuery<{ risorsa: Risorse }, AxiosError>(
    ["getRisorsa", id],
    () => axios.get(API_URL + "risorse/get/" + id).then((res) => res.data),
    {
      enabled: false,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      onSuccess: (data: { risorsa: Risorse }) => {
        queryClient.invalidateQueries();
        const { risorsa } = data;
        setLoaded(true);

        setValue("ClubId", risorsa.ClubId);
        setValue("denominazione", getString(risorsa.denominazione));
        setValue("indirizzo", getString(risorsa.indirizzo));
        setValue("descrizione", getString(risorsa.descrizione));
        setValue("Risorse_TipologieId", risorsa.Risorse_TipologieId);
        setValue("Risorse_SpecificheId", risorsa.Risorse_SpecificheId);
        setValue("idDomotica", risorsa.idDomotica);
        setValue("attiva", risorsa.attiva);
        setValue("dispoLuci", risorsa.dispoLuci);
        setValue("dispoRiscaldamento", risorsa.dispoRiscaldamento);
      },
      onError: (error: AxiosError) => {
        console.error(error);
        enqueueSnackbar("Si è verificato un errore!", {
          variant: "error",
        });
      },
    }
  );

  const { error, mutate } = useMutation(
    (data: Inputs) => {
      return axios.post(API_URL + "risorse/create", data);
    },
    {
      onSuccess: (data) => {
        const { id } = data.data;
        if (id) {
          queryClient.invalidateQueries();
          enqueueSnackbar("Risorsa Creato", { variant: "success" });
          navigate("/app/editRisorsa/" + id);
        }
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(error?.response?.data?.error ?? "Si è verificato un errore!", {
          variant: "error",
        });
      },
    }
  );

  const { mutate: mutateUpdate } = useMutation(
    (data: Inputs) => {
      return axios.post(API_URL + "risorse/update", { ...data });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries();
        enqueueSnackbar("Risorsa Aggiornata", { variant: "success" });
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(error?.response?.data?.error ?? "Si è verificato un errore!", {
          variant: "error",
        });
      },
    }
  );

  const {
    control,
    reset,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<Inputs>({
    mode: "all",
    resolver: yupResolver(schema),
  });
  const onSubmit = (data: Inputs) => {
    if (id !== undefined) mutateUpdate({ ...data, id: parseInt(id) });
    else if (ClubId) mutate({ ...data, ClubId: parseInt(ClubId) });
  };

  useEffect(() => {
    setLoaded(false);
    if (id === undefined) {
      reset(defaultValues);
      setLoaded(true);
    } else refetch();
  }, [reset, setValue, id, refetch]);

  const [animations, setAnimations] = useState(false);
  useEffect(() => {
    let timeout = 0;
    if (loaded) {
      timeout = window.setTimeout(() => setAnimations(true), 1);
    }
    return () => {
      if (timeout !== 0) clearTimeout(timeout);
    };
  }, [loaded]);

  let errorMsg = error?.response?.data?.error ?? "";
  const role = oraPrenotaStore((state) => state.role);

  let title = id === undefined ? "Crea Nuova Risorsa" : "Modifica Risorsa";
  const { data } = useQuery<
    unknown,
    AxiosError,
    { clubs: { id: number; codice: string; nome: string }[] }
  >(["getClubs", id], () => axios.get(API_URL + "club/clubs").then((res) => res.data), {
    enabled: role === "ADMIN",
  });

  const club = data?.clubs.find((c) => c.id === getValues("ClubId"));
  if (club) title = club.codice + " " + club.nome + " / " + getValues("denominazione");

  const { data: tipiSpec } = useQuery<
    { tipologie: RisorseTipologie[]; specifiche: RisorseSpecifiche[] },
    AxiosError
  >(["selectTipSpec"], () => axios.get(API_URL + "risorse/selectTipSpec").then((res) => res.data), {
    refetchOnWindowFocus: false,
    staleTime: 1e5,
    cacheTime: 1e5,
    onError: (error: AxiosError) => {
      enqueueSnackbar(error?.response?.data?.message ?? "Si è verificato un errore!", {
        variant: "error",
      });
    },
  });

  const setTitle = uiStore((state) => state.setTitle);
  useEffect(() => {
    setTitle(title);
  }, [setTitle, title]);

  if (!loaded) return <Loading />;

  if (clubId > 0) setValue("ClubId", clubId);
  const risorsaId = id ? parseInt(id) : undefined;

  return (
    <Box className={animations ? "" : "noAnimation"}>
      <Typography variant="h3" gutterBottom>
        {getValues("denominazione")}
      </Typography>
      <Tabs value={tabValue} onChange={handleChange} variant="scrollable" scrollButtons="auto">
        <Tab label="Dati di Base" {...a11yProps(0)} />
        {risorsaId && <Tab label="Dettagli Funzionali" {...a11yProps(1)} />}
        {risorsaId && <Tab label="Dispositivi" {...a11yProps(2)} />}
      </Tabs>

      <TabPanel value={tabValue} index={0}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container columnSpacing={1}>
            <Grid item xs={12}>
              <Controller
                name="attiva"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value}
                        onChange={(v) => setValue("attiva", v.target.checked)}
                      />
                    }
                    label="Attiva"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="denominazione"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="standard"
                    label="Denominazione"
                    error={Boolean(errors.denominazione)}
                    helperText={errors.denominazione?.message ?? " "}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="indirizzo"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    rows={3}
                    variant="standard"
                    label="Indirizzo"
                    error={Boolean(errors.indirizzo)}
                    helperText={errors.indirizzo?.message ?? " "}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="Risorse_TipologieId"
                control={control}
                defaultValue={1}
                render={({ field }) => (
                  <FormControl {...field} fullWidth variant="standard">
                    <InputLabel>Tipologia</InputLabel>
                    <Select
                      required
                      label="Tipologia"
                      value={field.value === null ? "" : field.value}
                      onChange={(e) =>
                        setValue("Risorse_TipologieId", parseInt("" + e.target.value))
                      }
                    >
                      {tipiSpec?.tipologie?.map((tip) => (
                        <MenuItem key={tip.id} value={tip.id}>
                          {tip.definizione}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="Risorse_SpecificheId"
                control={control}
                defaultValue={1}
                render={({ field }) => (
                  <FormControl {...field} fullWidth variant="standard">
                    <InputLabel>Specifica</InputLabel>
                    <Select
                      required
                      label="Specifica"
                      value={field.value === null ? "" : field.value}
                      onChange={(e) =>
                        setValue("Risorse_SpecificheId", parseInt("" + e.target.value))
                      }
                    >
                      {tipiSpec?.specifiche?.map((tip) => (
                        <MenuItem key={tip.id} value={tip.id}>
                          {tip.definizione}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Controller
                name="idDomotica"
                control={control}
                defaultValue={0}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    sx={{ width: "25ch" }}
                    type="number"
                    variant="standard"
                    label="Id Domotica (Vecchia)"
                    error={Boolean(errors.idDomotica)}
                    helperText={errors.idDomotica?.message ?? " "}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="dispoLuci"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value}
                        onChange={(v) => setValue("dispoLuci", v.target.checked)}
                      />
                    }
                    label="Luci disponibili"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="dispoRiscaldamento"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value}
                        onChange={(v) => setValue("dispoRiscaldamento", v.target.checked)}
                      />
                    }
                    label="Riscaldamento disponibile"
                  />
                )}
              />
            </Grid>
          </Grid>
          {!id && (
            <Button type="submit" variant="contained" sx={{ mt: 2 }}>
              Crea Risorsa
            </Button>
          )}
          {id && (
            <Button type="submit" variant="contained" sx={{ mt: 2 }}>
              Modifica Risorsa
            </Button>
          )}
          {errorMsg && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {errorMsg}
            </Alert>
          )}
        </form>
      </TabPanel>

      {risorsaId && (
        <TabPanel value={tabValue} index={1}>
          <TabellaDettagli risorsaId={risorsaId} />
        </TabPanel>
      )}

      {risorsaId && (
        <TabPanel value={tabValue} index={2}>
          <DispositiviUi risorseId={risorsaId} clubId={clubId} />
        </TabPanel>
      )}
    </Box>
  );
}
